import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { barsApi } from '@/api/endpoints/bars';
import { EventCard } from '@/components/domain';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageSpinner,
  Button,
} from '@/components/ui';
import { ArrowLeft, Plus, Calendar } from 'lucide-react';

export function EventsManagePage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['bar', id],
    queryFn: () => barsApi.getById(id!),
    enabled: !!id,
  });

  const bar = data?.bar;
  const events = bar?.events || [];

  if (isLoading) return <PageSpinner />;

  if (!bar) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--color-muted)]">Bar not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to={`/admin/bars/${id}`}
        className="flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to {bar.name}
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Manage Events</h1>
          <p className="mt-1 text-[var(--color-muted)]">{bar.name}</p>
        </div>
        <Button disabled>
          <Plus className="mr-2 h-4 w-4" />
          Add Event
        </Button>
      </div>

      {/* Coming soon notice */}
      <Card>
        <CardContent className="p-4 text-center text-sm text-[var(--color-muted)]">
          Full CRUD for events coming soon. For now, events are managed via AI extraction.
        </CardContent>
      </Card>

      {/* Events list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Events ({events.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-center text-[var(--color-muted)]">No events</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {events.map((event) => (
                <EventCard key={event.id} event={event} showBar={false} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
